import { Info } from 'lucide-solid';
import { type JSX, Show, createEffect, createSignal, onCleanup } from 'solid-js';
import { Portal } from 'solid-js/web';
import { floatingMount } from './floatingLayer';

interface InfoHintProps {
  children: JSX.Element;
  label?: string;
  size?: number;
  width?: number;
}

export function InfoHint(props: InfoHintProps): JSX.Element {
  const [open, setOpen] = createSignal(false);
  const [pos, setPos] = createSignal<{ top: number; left: number; flip: boolean }>({
    top: 0,
    left: 0,
    flip: false,
  });
  let triggerRef: HTMLButtonElement | undefined;
  const width = () => props.width ?? 248;

  const reposition = () => {
    if (!triggerRef) return;
    const r = triggerRef.getBoundingClientRect();
    const w = width();
    const gap = 8;
    let left = r.left + r.width / 2 - w / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - w - 8));
    const roomBelow = window.innerHeight - r.bottom;
    const flip = roomBelow < 160 && r.top > roomBelow;
    const top = flip ? r.top - gap : r.bottom + gap;
    setPos({ top, left, flip });
  };

  const show = () => {
    reposition();
    setOpen(true);
  };
  const hide = () => setOpen(false);

  createEffect(() => {
    if (!open()) return;
    const handler = () => reposition();
    window.addEventListener('scroll', handler, true);
    window.addEventListener('resize', handler);
    onCleanup(() => {
      window.removeEventListener('scroll', handler, true);
      window.removeEventListener('resize', handler);
    });
  });

  return (
    <span
      class="inline-flex align-middle"
      onMouseEnter={show}
      onMouseLeave={hide}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-label={props.label ?? 'Подробнее'}
        class="press inline-flex items-center justify-center rounded-full text-tertiary hover:text-secondary transition-colors"
        onFocus={show}
        onBlur={hide}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          open() ? hide() : show();
        }}
      >
        <Info size={props.size ?? 13} stroke-width={2.2} />
      </button>
      <Show when={open()}>
        <Portal mount={floatingMount(triggerRef)}>
          <span
            data-floating-layer=""
            role="tooltip"
            class="fixed z-[1400] rounded-md px-2.5 py-2 text-[11.5px] leading-[1.45] font-normal text-left pointer-events-none"
            style={{
              width: `${width()}px`,
              left: `${pos().left}px`,
              top: `${pos().top}px`,
              ...(pos().flip ? { transform: 'translateY(-100%)' } : {}),
              background: 'var(--color-bg-elevated)',
              color: 'var(--color-text-secondary)',
              'box-shadow':
                '0 10px 28px rgba(0,0,0,0.38), inset 0 0 0 1px var(--color-border-hairline)',
              'white-space': 'normal',
              'text-transform': 'none',
              'letter-spacing': 'normal',
            }}
          >
            {props.children}
          </span>
        </Portal>
      </Show>
    </span>
  );
}
